<template>
  <Teleport to="body">
    <div v-show="show" class="tqf-layer" aria-hidden="true">
      <div
        v-if="menuOpen"
        class="tqf-backdrop"
        @click="menuOpen = false"
      />
      <div
        ref="rootEl"
        class="tqf-root"
        :style="rootStyle"
      >
        <div
          v-show="menuOpen"
          class="tqf-menu"
          :class="menuAbove ? 'tqf-menu--above' : 'tqf-menu--below'"
        >
          <button type="button" class="tqf-action tqf-action--buy" @click="onBuy">
            去抢购
          </button>
          <button type="button" class="tqf-action tqf-action--sell" @click="onSell">
            去寄售
          </button>
        </div>
        <div
          class="tqf-trigger"
          role="button"
          tabindex="0"
          @click.stop.prevent="onTriggerTap"
          @pointerdown="onTriggerPointerDown"
          @pointermove="onTriggerPointerMove"
          @pointerup="onTriggerPointerUp"
          @pointercancel="onTriggerPointerCancel"
        >
          <van-icon name="setting-o" size="20" class="tqf-gear" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { getLstToken, getLstPhone } from "@/utils/lstSessionStorage";
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { showErr } from "@/utils/notify";
const props = defineProps({
    show: { type: Boolean, default: true },
    uniqueGId: { type: String, default: "" },
    albumId: { type: String, default: "" },
    albumName: { type: String, default: "" },
    price: { type: String, default: "" },
    gNum: { type: String, default: "" },
    issueNum: { type: String, default: "" },
    soldNum: { type: String, default: "" }
});
const route = useRoute();
const router = useRouter();
const STORAGE_KEY = "lst_trade_fab_pos_v1";
const FAB = 48;
const MENU_EXTRA = 132;
const DRAG_THRESHOLD = 14;
const rootEl = ref(null);
const pos = ref({ x: 0, y: 0 });
const menuOpen = ref(false);
const menuAbove = ref(true);
const suppressNextTap = ref(false);
let dragActive = false;
let dragMoved = false;
let startClientX = 0;
let startClientY = 0;
let originX = 0;
let originY = 0;
let activePointerId = null;
const rootStyle = computed(() => ({
    left: `${pos.value.x}px`,
    top: `${pos.value.y}px`
}));
const MIN_EDGE = 6;
function tabbarBlockHeight() {
    if (typeof document === "undefined")
        return 56;
    const el = document.querySelector(".app .van-tabbar") || document.querySelector(".van-tabbar");
    if (el) {
        const h = el.getBoundingClientRect().height;
        if (h > 1)
            return h;
    }
    return 50;
}
function measureFab() {
    const el = rootEl.value;
    if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 2 && r.height > 2)
            return { w: r.width, h: r.height };
    }
    if (menuOpen.value)
        return { w: 104, h: 168 };
    return { w: 48, h: 48 };
}
function clampPos(x, y) {
    if (typeof window === "undefined")
        return { x: 0, y: 0 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { w, h } = measureFab();
    const bottomPad = tabbarBlockHeight() + MIN_EDGE;
    const topSafe = MIN_EDGE;
    const maxX = Math.max(MIN_EDGE, vw - w - MIN_EDGE);
    const maxY = Math.max(topSafe, vh - h - bottomPad);
    return {
        x: Math.min(maxX, Math.max(MIN_EDGE, x)),
        y: Math.min(maxY, Math.max(topSafe, y))
    };
}
function defaultPosition() {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const bottomPad = tabbarBlockHeight() + MIN_EDGE;
    return clampPos(vw - FAB - 14, vh - FAB - bottomPad);
}
function loadPosition() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return defaultPosition();
        const p = JSON.parse(raw);
        if (typeof p.x !== "number" || typeof p.y !== "number")
            return defaultPosition();
        return clampPos(p.x, p.y);
    }
    catch {
        return defaultPosition();
    }
}
function savePosition() {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value));
    }
    catch {
    }
}
function layoutMenuDirection() {
    const el = rootEl.value;
    if (!el)
        return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const cy = r.top + r.height / 2;
    const spaceAbove = r.top;
    const spaceBelow = vh - r.bottom;
    const need = MENU_EXTRA;
    if (spaceBelow >= need + 8 && cy < vh * 0.38) {
        menuAbove.value = false;
        return;
    }
    if (spaceAbove >= need + 8 && cy > vh * 0.62) {
        menuAbove.value = true;
        return;
    }
    menuAbove.value = spaceAbove >= spaceBelow;
}
function onTriggerPointerDown(e) {
    if (e.button != null && e.button !== 0)
        return;
    suppressNextTap.value = false;
    dragActive = true;
    dragMoved = false;
    startClientX = e.clientX;
    startClientY = e.clientY;
    originX = pos.value.x;
    originY = pos.value.y;
    activePointerId = e.pointerId;
    try {
        e.currentTarget.setPointerCapture(e.pointerId);
    }
    catch {
    }
}
function onTriggerPointerMove(e) {
    if (!dragActive || e.pointerId !== activePointerId)
        return;
    const dx = e.clientX - startClientX;
    const dy = e.clientY - startClientY;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
        dragMoved = true;
        if (menuOpen.value)
            menuOpen.value = false;
        pos.value = clampPos(originX + dx, originY + dy);
    }
}
function endDrag(e) {
    if (!dragActive)
        return;
    if (e.pointerId !== activePointerId)
        return;
    dragActive = false;
    activePointerId = null;
    try {
        e.currentTarget.releasePointerCapture(e.pointerId);
    }
    catch {
    }
    if (dragMoved) {
        savePosition();
        suppressNextTap.value = true;
    }
}
function onTriggerTap() {
    if (suppressNextTap.value) {
        suppressNextTap.value = false;
        return;
    }
    const { token } = getAuth();
    if (!token) {
        showErr("\u8BF7\u5148\u767B\u5F55");
        return;
    }
    menuOpen.value = !menuOpen.value;
    if (menuOpen.value) {
        requestAnimationFrame(() => layoutMenuDirection());
    }
}
function onTriggerPointerUp(e) {
    endDrag(e);
}
function onTriggerPointerCancel(e) {
    dragActive = false;
    activePointerId = null;
    dragMoved = false;
    try {
        e.currentTarget.releasePointerCapture(e.pointerId);
    }
    catch {
    }
}
function getAuth() {
    return {
        token: getLstToken(),
        phone: getLstPhone()
    };
}
const gidTrim = () => String(props.uniqueGId || "").trim();
const aidTrim = () => String(props.albumId || "").trim();
function onBuy() {
    menuOpen.value = false;
    const g = gidTrim();
    const aid = aidTrim();
    const baseQ = {
        ...aid ? { albumId: aid } : {},
        ...props.albumName ? { albumName: props.albumName } : {},
        ...props.price ? { price: props.price } : {},
        ...props.issueNum ? { issueNum: props.issueNum } : {},
        ...props.soldNum ? { soldNum: props.soldNum } : {}
    };
    if (g) {
        router.push({
            path: "/purchase",
            query: {
                ...baseQ,
                gId: g,
                ...props.gNum ? { gNum: props.gNum } : {}
            }
        });
    }
    else if (aid) {
        router.push({ path: "/purchase", query: baseQ });
    }
    else {
        router.push({ path: "/purchase" });
    }
}
function onSell() {
    menuOpen.value = false;
    const g = gidTrim();
    const aid = aidTrim();
    if (g) {
        router.push({
            path: "/shelves",
            query: {
                ...aid ? { albumId: aid } : {},
                ...props.albumName ? { albumName: props.albumName } : {},
                gId: g
            }
        });
    }
    else if (aid) {
        router.push({
            path: "/shelves",
            query: {
                albumId: aid,
                ...props.albumName ? { albumName: props.albumName } : {}
            }
        });
    }
    else {
        router.push({ path: "/shelves" });
    }
}
watch(menuOpen, (open) => {
    if (open) {
        nextTick(() => {
            pos.value = clampPos(pos.value.x, pos.value.y);
            requestAnimationFrame(() => layoutMenuDirection());
        });
    }
});
watch(() => props.show, (v) => {
    if (!v)
        menuOpen.value = false;
});
watch(() => route.fullPath, () => {
    menuOpen.value = false;
});
function onResize() {
    pos.value = clampPos(pos.value.x, pos.value.y);
    if (menuOpen.value)
        layoutMenuDirection();
}
onMounted(() => {
    pos.value = loadPosition();
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
});
onUnmounted(() => {
    window.removeEventListener("resize", onResize);
    window.visualViewport?.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.tqf-layer {
  position: fixed;
  inset: 0;
  z-index: 2100;
  pointer-events: none;
}
.tqf-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: auto;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
}
.tqf-root {
  position: fixed;
  z-index: 1;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
  min-width: 48px;
}
.tqf-menu {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  pointer-events: auto;
  margin-bottom: 8px;
  width: 104px;
}
.tqf-menu--above {
  flex-direction: column;
  margin-bottom: 8px;
  margin-top: 0;
  order: 0;
}
.tqf-menu--below {
  flex-direction: column;
  margin-top: 8px;
  margin-bottom: 0;
  order: 2;
}
.tqf-action {
  display: block;
  width: 100%;
  padding: 10px 8px;
  border: none;
  border-radius: 22px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 3px 12px rgba(15, 23, 42, 0.12);
  transition: transform 0.12s ease, opacity 0.12s ease;
}
.tqf-action:active {
  transform: scale(0.97);
  opacity: 0.92;
}
.tqf-action--buy {
  color: #fff;
  background: linear-gradient(145deg, #5b8cff, #3b6fef);
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.tqf-action--sell {
  color: #fff;
  background: linear-gradient(145deg, #ff8c42, #ff6b35);
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.tqf-trigger {
  order: 1;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(145deg, #ff5a4d, #e02020);
  box-shadow:
    0 4px 16px rgba(224, 32, 32, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  touch-action: none;
  color: #fff;
}
.tqf-trigger:active {
  opacity: 0.92;
  transform: scale(0.97);
}
.tqf-gear {
  opacity: 0.95;
}
</style>
